import React, { useContext, useEffect, useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import SendIcon from '@mui/icons-material/Send';
import { Button as MuiButton } from '@mui/material';
import UploadLinkModal from './UploadLinkModal';
import { formatDate } from '../../utils/formatDate';
import { calculateAge } from '../../utils/calculateAge';
import BirthdayIndicator from '../../components/BirthdayIndicator/BirthdayIndicator';
import { removeTags } from '../../utils/removeTags';
import { camelCaseToText } from '../../utils/camelCaseToText';
import { createUploadLink } from '../../utils/api/files';
import './ProfileBox.scss';
import { Client } from '../../interfaces/Client';
import InputWrapper from '../../components/inputWrapper/InputWrapper';
import { User } from '../../interfaces/User';
import Select from 'react-select';
import { UserProfileContext } from '../../context/userProfileContext';
import { Onboarding } from '../../interfaces/Onboarding';
import { getSignatures } from '../../utils/api/signatures';
import { SignatureStatus, SignedDocument } from '../../interfaces/SignedDocument';
import AddEmployer from '../employers/AddEmployer';
import EmployerAutocomplete from '../../components/EmployerAutocomplete';
import { Employer } from '../../interfaces/Employer';
import { editClient } from '../../utils/api/clients';
import { useTranslation } from 'react-i18next';
import { isPOAValid } from '../../utils/api/pension';
import globalErrorHandler from '../../utils/globalErrorHandler';
import ResubmitInsurnace from './insurance/insurance-resubmit';
import { FamilyMemberType, Polywizz } from '../../interfaces/Insurance';
import { getPolywizz } from '../../utils/api/insurance';

interface ProfileBoxProps {
    isOnboarding: boolean;
    client?: Client;
}

const ProfileBox = (props: ProfileBoxProps) => {
    const { t } = useTranslation('profile');

    const [showUploadLink, setShowUploadLink] = useState(false);
    const [newEmployerName, setNewEmployerName] = useState('');
    const [signedDocument, setSignedDocument] = useState<SignedDocument | undefined>();
    const [insurnaceSignedDocument, setInsurnaceSignedDocument] = useState<SignedDocument | undefined>();
    const [allSignedDocuments, setAllSignedDocuments] = useState<SignedDocument[]>([]);
    const [linkInfo, setLinkInfo] = useState<{
        link?: string;
        text?: string;
        outOfDate?: boolean;
        expiryDate?: Date | undefined;
    }>({});
    const { profileContextData, onEditRound, users, allUsers, getUserById } = useContext(UserProfileContext);
    const [client, setClient] = useState<Client>(props.client || profileContextData?.client);
    const selectedModule = profileContextData?.selectedModule;
    const allowedStatusesOptions = selectedModule && selectedModule.allowedStatuses ? Object.values(selectedModule.allowedStatuses) : [];
    const selectedModuleName = selectedModule?.data?.moduleName;
    const [onboarding, setOnboarding] = useState<Onboarding>(profileContextData?.currentRound as Onboarding);
    // const onboarding = profileContextData?.currentRound as Onboarding;
    const [showEmployerAutocomplete, setShowEmployerAutocomplete] = useState(false);

    const [interviewers, setInterviewers] = useState<User[]>(
        users.filter((rep: User) => rep.roles.includes('interviewer') || (rep && rep._id === onboarding?.rep)),
    );

    const [showAddEmployer, setShowAddEmployer] = useState(false);
    const [userInputTextReasonOther, setUserInputTextReasonOther] = useState('');
    const [polywizz, setPolywizz] = useState<Polywizz | null>(null);

    useEffect(() => {
        if (client) {
            setOnboarding(profileContextData?.currentRound as Onboarding);
        }
    }, [profileContextData?.currentRound]);

    useEffect(() => {
        if (props.isOnboarding) setInterviewers(users.filter((rep: User) => rep.roles.includes('interviewer') || rep._id === onboarding?.rep));
    }, [users, props.client, profileContextData?.client]);

    const getPOASignature = async () => {
        try {
            const { signedDocuments } = await getSignatures({
                clientId: client._id,
                documentTypes:
                    'powerOfAttorney,powerOfAttorneyMislaka,powerOfAttorneyInsurance,powerOfAttorneyPensionAndInsurance,powerOfAttorneyInsuranceMountain,powerOfAttorneyInsuranceWithTerms,powerOfAttorneyNoTax',
            }); //TODO: add here a flag for witch module we need the reps list.

            const pensionPowerOfAttorney = signedDocuments
                .filter((doc: SignedDocument) => ['powerOfAttorney', 'powerOfAttorneyMislaka'].includes(doc.documentType) && doc.clientSignedDate)
                .sort((a: SignedDocument, b: SignedDocument) => new Date(b.clientSignedDate).getTime() - new Date(a.clientSignedDate).getTime());
            setSignedDocument(pensionPowerOfAttorney[0]);

            const insurancePowerOfAttorney = signedDocuments
                .filter(
                    (doc: SignedDocument) =>
                        ['powerOfAttorney', 'powerOfAttorneyInsuranceMountain'].includes(doc.documentType) && doc.clientSignedDate,
                )
                .sort((a: SignedDocument, b: SignedDocument) => new Date(b.clientSignedDate).getTime() - new Date(a.clientSignedDate).getTime());
            setInsurnaceSignedDocument(insurancePowerOfAttorney[0]);

            // Filter for the requested document types and sort by date (signed or created)
            const requestedDocuments = signedDocuments.sort((a: SignedDocument, b: SignedDocument) => {
                const dateA = a.clientSignedDate ? new Date(a.clientSignedDate).getTime() : new Date(a.createdAt).getTime();
                const dateB = b.clientSignedDate ? new Date(b.clientSignedDate).getTime() : new Date(b.createdAt).getTime();
                return dateB - dateA;
            });
            setAllSignedDocuments(requestedDocuments);
        } catch (err) {
            globalErrorHandler(err);
        }
    };

    const getInsurancePOAExpiryDate = () => {
        if (!insurnaceSignedDocument) return null;

        const expiryDate = new Date(insurnaceSignedDocument.clientSignedDate);
        expiryDate.setMonth(expiryDate.getMonth() + 1);
        return expiryDate;
    };

    const getSignatureLinkInfo = async () => {
        if (!signedDocument) return {};
        // const { success: isValid, validThrough } = await isPOAValid(client._id);

        const validThrough = new Date(signedDocument.createdAt);
        validThrough.setMonth(validThrough.getMonth() + 3);
        const isValid = validThrough > new Date();

        const expiryDate: Date | undefined = validThrough ? new Date(validThrough) : undefined;
        let linkInfo: {
            link?: string;
            text?: string;
            outOfDate?: boolean;
            expiryDate?: Date | undefined;
        } = {};
        if (signedDocument.file) {
            linkInfo = {
                link: signedDocument.file.link,
                text: 'Download signed document',
                expiryDate,
            };
        } else if (signedDocument.agentSigningLink) {
            linkInfo = {
                link: signedDocument.agentSigningLink,
                text: 'Agent signing link',
                expiryDate,
            };
        } else if (signedDocument.clientSigningLink) {
            linkInfo = {
                link: signedDocument.clientSigningLink,
                text: 'Client signing link',
                expiryDate,
            };
        } else {
            return {};
        }
        if (!isValid) {
            linkInfo.outOfDate = true;
        }
        setLinkInfo(linkInfo);
    };

    const getPolywizzData = async () => {
        const { items: polywizzItems } = await getPolywizz({ clientId: client._id });

        setPolywizz(polywizzItems.find((item: Polywizz) => item.familyMemberType === FamilyMemberType.client));
    };

    useEffect(() => {
        if (props.isOnboarding) getPOASignature();
        getPolywizzData();
    }, []);

    useEffect(() => {
        if (props.isOnboarding) getSignatureLinkInfo();
    }, [signedDocument]);

    useEffect(() => {
        setClient(props.client || profileContextData.client);
    }, [props.client]);

    const getPhoneNumber = (client?: Client) => {
        const phoneNumber: string = client?.phoneNumber?.replace('+972', '0') || '';
        return `${phoneNumber.substring(0, 3)}-${phoneNumber.substring(3)}`;
    };

    if (!onboarding && props.isOnboarding) return <></>;

    const handleNewEmployer = (name: string) => {
        setNewEmployerName(name);
        setShowEmployerAutocomplete(false);
        setShowAddEmployer(true);
    };

    const onExistingEmployerSelected = async (employer?: Employer) => {
        setShowEmployerAutocomplete(false);
        if (!employer) return;
        try {
            await editClient(client._id, { employer: employer._id });
            setClient({ ...client, employer });
        } catch (err) {
            globalErrorHandler(err);
        } finally {
            setShowAddEmployer(false);
        }
    };

    const onStatusChanged = (status: string) => {
        if (['postZoom', 'postZoomMissingFiles'].includes(status)) {
            if (!client.employmentStatus) {
                alert('Employment status not set');
                return;
            } else if (!client.employer && requiresEmployer) {
                alert('Please select an employer before changing status');
                return;
            }
        }
        if (status === 'irrelevantClient') {
            const confirmIrrelevant = window.confirm(t("Are you sure you want to change the status to 'Irrlevant Client'?"));
            if (!confirmIrrelevant) {
                return;
            }
        }
        const prevStatus = onboarding.status;
        setOnboarding({ ...onboarding, status });

        onEditRound({ status }, selectedModuleName, onboarding._id, undefined, () => {
            setOnboarding({ ...onboarding, status: prevStatus });
        });
    };

    const onInterviewerChanged = (userId: string) => {
        const prevRep = onboarding.rep;
        setOnboarding({ ...onboarding, rep: userId });
        onEditRound({ rep: userId }, selectedModuleName, onboarding._id, undefined, () => setOnboarding({ ...onboarding, rep: prevRep }));
    };

    const requiresEmployer =
        client.employmentStatus && !['selfEmployed', 'unemployed', 'retired', 'student', 'unpaidLeave'].includes(client.employmentStatus);

    return (
        <Card className="text-center user-profile-box">
            <Card.Body>
                <div className="box">
                    {!props.isOnboarding ? (
                        <div className="buttons-row">
                            <a href={`/clients/profile?clientId=${client?._id}`} target="_blank" rel="noopener noreferrer">
                                <Button variant="success">
                                    <i className="fe-user-check" /> Client Profile
                                </Button>
                            </a>
                        </div>
                    ) : (
                        <></>
                    )}

                    {client ? (
                        <div className="text-start">
                            {!props.isOnboarding ? (
                                <div className="info-section">
                                    <p className="info-field">
                                        <strong>Client Name:</strong>
                                        <span className="info-value">
                                            {client?.firstName} {client?.lastName}
                                        </span>
                                    </p>

                                    <p className="info-field">
                                        <strong>Module Status:</strong>
                                        <span className="info-value">{camelCaseToText(client.pensionStatus)}</span>
                                    </p>

                                    <p className="info-field">
                                        <strong>ID:</strong>
                                        <span className="info-value">{client.idNumber}</span>
                                    </p>

                                    <p className="info-field">
                                        <strong>Mobile:</strong>
                                        <span className="info-value">{getPhoneNumber(client)}</span>
                                    </p>

                                    <p className="info-field">
                                        <strong>Email:</strong>
                                        <span className="info-value">{client.email}</span>
                                    </p>

                                    <p className="info-field">
                                        <strong>Employer:</strong>
                                        <span className="info-value">{client?.employer?.name}</span>
                                    </p>
                                </div>
                            ) : (
                                <></>
                            )}

                            {props.isOnboarding ? (
                                <>
                                    <div className="select-area">
                                        {client ? (
                                            <>
                                                {allowedStatusesOptions && allowedStatusesOptions.length > 0 ? (
                                                    <InputWrapper status={{ text: '' }} label="Status" wrapperClass="input-field select-field">
                                                        <Select
                                                            options={allowedStatusesOptions.map((option: any) => ({
                                                                label: camelCaseToText(option),
                                                                value: option,
                                                            }))}
                                                            onChange={(item: any) => onStatusChanged(item.value)}
                                                            value={{
                                                                label: allowedStatusesOptions.includes(onboarding.status)
                                                                    ? camelCaseToText(onboarding.status)
                                                                    : 'Unknown',
                                                                value: onboarding.status,
                                                            }}
                                                            className="d-flex flex-wrap"
                                                            styles={{
                                                                menu: (base) => ({
                                                                    ...base,
                                                                    width: '100%',
                                                                }),
                                                                control: (base) => ({
                                                                    ...base,
                                                                    width: '100%',
                                                                }),
                                                            }}
                                                        />
                                                    </InputWrapper>
                                                ) : null}

                                                {interviewers ? (
                                                    <div style={{ marginTop: '1rem' }}>
                                                        <InputWrapper
                                                            status={{ text: '' }}
                                                            label={'Interviewer'}
                                                            wrapperClass="input-field select-field mt3"
                                                        >
                                                            <Select
                                                                options={interviewers.map((interviewer: User) => ({
                                                                    label: interviewer.fullName || 'Unknown',
                                                                    value: interviewer._id,
                                                                }))}
                                                                onChange={(item: any) => onInterviewerChanged(item.value)}
                                                                value={{
                                                                    label: getUserById(onboarding.rep)?.fullName || 'Unknown',
                                                                    value: onboarding.rep,
                                                                }}
                                                                className="d-flex flex-wrap"
                                                                styles={{
                                                                    menu: (base) => ({
                                                                        ...base,
                                                                        width: '100%',
                                                                    }),
                                                                    control: (base) => ({
                                                                        ...base,
                                                                        width: '100%',
                                                                    }),
                                                                }}
                                                            />
                                                        </InputWrapper>
                                                    </div>
                                                ) : null}
                                            </>
                                        ) : (
                                            <div>No data yet...</div>
                                        )}
                                    </div>

                                    <div className="info-section">
                                        <p className="info-field">
                                            <strong>Payslip Password:</strong>
                                            <span className="info-value">{client?.payslipPasswords.join(', ') || 'no password'}</span>
                                        </p>

                                        {/* //TODO: add this also to 'files' tab  */}
                                        <p className="info-field" onClick={(label) => setShowUploadLink(true)}>
                                            <strong>Upload files link:</strong>
                                            <span className="info-value">
                                                <a href="#">
                                                    <strong>Get link</strong>
                                                    <i className="ms-2 mdi mdi-link-variant" />
                                                </a>
                                            </span>
                                        </p>

                                        {onboarding.zoomMeeting ? (
                                            <p className="info-field">
                                                <strong>Onboarding Zoom:</strong>
                                                <div className="info-value">
                                                    <div>{formatDate(onboarding.zoomMeeting.startTime, false, false)}</div>
                                                    <div>
                                                        <a href={onboarding.zoomMeeting.zoomLink} target="_blank" rel="noreferrer">
                                                            Click to connect
                                                            <i className="ms-2 mdi mdi-video" />
                                                        </a>
                                                        <a
                                                            href={onboarding.zoomMeeting.cancelUrl}
                                                            target="_blank"
                                                            className="cancel-zoom ms-2"
                                                            rel="noreferrer"
                                                        >
                                                            Cancel Zoom
                                                        </a>
                                                    </div>
                                                </div>
                                            </p>
                                        ) : null}

                                        <p className="info-field">
                                            <strong>Client Source:</strong>
                                            <span className="info-value">
                                                {client?.source.sourceType}{' '}
                                                {client?.source
                                                    ? `(${client.source.campaign || client.source.referredBy?.employer?.name || ''})`
                                                    : ''}
                                            </span>
                                        </p>

                                        {client?.onboardingRemarks ? (
                                            <p className="info-field">
                                                <strong>Onboarding Remarks:</strong>
                                                <span className="info-value">{removeTags(client.onboardingRemarks)}</span>
                                            </p>
                                        ) : null}
                                    </div>

                                    <div className="info-section">
                                        {allSignedDocuments && allSignedDocuments.length > 0 ? (
                                            <div className="info-field">
                                                <strong>Power of Attorney:</strong>
                                                <div className="info-value">
                                                    {allSignedDocuments.map((doc: SignedDocument, index: number) => {
                                                        const isSigned = !!doc.clientSignedDate;

                                                        // Calculate expiration dates based on document type (only if signed)
                                                        const getExpirationDates = (docType: string, signedDate: Date) => {
                                                            const insuranceExpiry = new Date(signedDate);
                                                            insuranceExpiry.setDate(insuranceExpiry.getDate() + 30);

                                                            const pensionExpiry = new Date(signedDate);
                                                            pensionExpiry.setDate(pensionExpiry.getDate() + 90);

                                                            switch (docType) {
                                                                case 'powerOfAttorney':
                                                                case 'powerOfAttorneyNoTax':
                                                                case 'powerOfAttorneyPensionAndInsurance':
                                                                    return {
                                                                        insurance: insuranceExpiry,
                                                                        pension: pensionExpiry,
                                                                        showBoth: true,
                                                                    };
                                                                case 'powerOfAttorneyInsurance':
                                                                case 'powerOfAttorneyInsuranceWithTerms':
                                                                    return {
                                                                        insurance: insuranceExpiry,
                                                                        showBoth: false,
                                                                    };
                                                                case 'powerOfAttorneyMislaka':
                                                                    return {
                                                                        pension: pensionExpiry,
                                                                        showBoth: false,
                                                                    };
                                                                default:
                                                                    return { showBoth: false };
                                                            }
                                                        };

                                                        const expirations = isSigned
                                                            ? getExpirationDates(doc.documentType, doc.clientSignedDate)
                                                            : { showBoth: false };
                                                        const now = new Date();

                                                        return (
                                                            <div key={doc._id} style={{ marginBottom: '0.5rem' }}>
                                                                <div>
                                                                    <strong>
                                                                        {camelCaseToText(doc.documentType.replace('powerOfAttorney', '') || 'Full')}
                                                                    </strong>
                                                                    {!isSigned && (
                                                                        <span
                                                                            className="text-warning"
                                                                            style={{ marginLeft: '0.5rem', fontSize: '0.85em' }}
                                                                        >
                                                                            (Pending signature)
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {isSigned ? (
                                                                    <div style={{ fontSize: '0.85em' }}>
                                                                        Signed: {formatDate(doc.clientSignedDate, true, false)}
                                                                    </div>
                                                                ) : (
                                                                    <div style={{ fontSize: '0.85em', color: '#666' }}>
                                                                        Created: {formatDate(new Date(doc.createdAt), true, false)}
                                                                    </div>
                                                                )}
                                                                {isSigned && expirations.showBoth ? (
                                                                    <>
                                                                        <div
                                                                            className={`${expirations.insurance && expirations.insurance < now ? 'text-danger' : ''}`}
                                                                            style={{ fontSize: '0.85em' }}
                                                                        >
                                                                            Insurance expiration: {formatDate(expirations.insurance!, true, false)}
                                                                        </div>
                                                                        <div
                                                                            className={`${expirations.pension && expirations.pension < now ? 'text-danger' : ''}`}
                                                                            style={{ fontSize: '0.85em' }}
                                                                        >
                                                                            Pension expiration: {formatDate(expirations.pension!, true, false)}
                                                                        </div>
                                                                    </>
                                                                ) : isSigned && expirations.insurance ? (
                                                                    <div
                                                                        className={`${expirations.insurance < now ? 'text-danger' : ''}`}
                                                                        style={{ fontSize: '0.85em' }}
                                                                    >
                                                                        Expiration: {formatDate(expirations.insurance, true, false)}
                                                                    </div>
                                                                ) : isSigned && expirations.pension ? (
                                                                    <div
                                                                        className={`${expirations.pension < now ? 'text-danger' : ''}`}
                                                                        style={{ fontSize: '0.85em' }}
                                                                    >
                                                                        Expiration: {formatDate(expirations.pension, true, false)}
                                                                    </div>
                                                                ) : null}
                                                                {!isSigned && doc.clientSigningLink && (
                                                                    <a
                                                                        href={doc.clientSigningLink}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                        style={{ fontSize: '0.9em' }}
                                                                    >
                                                                        Client signing link
                                                                    </a>
                                                                )}
                                                                {doc.agentInvitationId &&
                                                                    doc.agentSigningLink &&
                                                                    doc.signatureStatus === SignatureStatus.ClientSigned && (
                                                                        <a
                                                                            href={doc.agentSigningLink}
                                                                            target="_blank"
                                                                            rel="noreferrer"
                                                                            style={{ fontSize: '0.9em' }}
                                                                        >
                                                                            Agent signing link
                                                                            <i className="ms-2 mdi mdi-link-variant" />
                                                                        </a>
                                                                    )}
                                                                {doc.file?.link && (
                                                                    <a
                                                                        href={doc.file.link}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                        style={{
                                                                            fontSize: '0.9em',
                                                                            marginLeft: doc.clientSigningLink && !isSigned ? '1rem' : 0,
                                                                        }}
                                                                    >
                                                                        Download document
                                                                    </a>
                                                                )}
                                                                {index < allSignedDocuments.length - 1 && <hr style={{ margin: '0.5rem 0' }} />}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ) : null}

                                        <p className="info-field">
                                            <strong>Polywizz status:</strong>
                                            <span className="info-value">
                                                {polywizz?.status ? camelCaseToText(polywizz.status) : 'Not started'}{' '}
                                                {!!polywizz && (
                                                    <>
                                                        <ResubmitInsurnace
                                                            client={client}
                                                            polywizz={polywizz}
                                                            familyMemberType={FamilyMemberType.client}
                                                            isMountainDate={true}
                                                        />
                                                        {(() => {
                                                            const insurancePOAExpiryDate = getInsurancePOAExpiryDate();
                                                            return insurancePOAExpiryDate !== null ? (
                                                                <div className={`${insurancePOAExpiryDate < new Date() ? 'text-danger' : ''}`}>
                                                                    (expiration: {formatDate(insurancePOAExpiryDate, true, false)})
                                                                </div>
                                                            ) : null;
                                                        })()}
                                                    </>
                                                )}
                                            </span>
                                        </p>

                                        <p className="info-field">
                                            <strong>Employer:</strong>
                                            <span className="info-value">
                                                {!client.employmentStatus && <span>Employment status not set</span>}
                                                {!requiresEmployer && !!client.employmentStatus && (
                                                    <span>Not required ({camelCaseToText(client.employmentStatus).toLowerCase()})</span>
                                                )}
                                                {requiresEmployer && client.employer && (
                                                    <>
                                                        <span>{client?.employer?.name}</span>
                                                        <span className="edit" onClick={() => setShowEmployerAutocomplete(!showEmployerAutocomplete)}>
                                                            <i className="mdi mdi-square-edit-outline" />{' '}
                                                            {showEmployerAutocomplete ? 'Close' : 'Edit'}
                                                        </span>
                                                    </>
                                                )}
                                                {(showEmployerAutocomplete || (requiresEmployer && !client.employer)) && (
                                                    <div className="mt-2">
                                                        <EmployerAutocomplete
                                                            value={client?.employer?.name}
                                                            handleFreeSearch={handleNewEmployer}
                                                            onAutocompleteSelect={onExistingEmployerSelected}
                                                        />
                                                    </div>
                                                )}
                                            </span>
                                        </p>

                                        <p className="info-field">
                                            <strong>Preferred modules:</strong>
                                            <span className="info-value">
                                                {client.modulesOrder
                                                    ?.filter((item) => item.isPreferred)
                                                    .map((item, index) => (
                                                        <span key={index} className="me-2">
                                                            {item.moduleName}
                                                        </span>
                                                    ))}
                                            </span>
                                        </p>

                                        <p className="info-field">
                                            <strong>Banks:</strong>
                                            <span className="info-value">
                                                {client.banks?.map((bank: string, index: number) => (
                                                    <span key={index} className="me-2">
                                                        {bank}
                                                    </span>
                                                ))}
                                            </span>
                                        </p>
                                    </div>

                                    <UploadLinkModal
                                        show={showUploadLink}
                                        onHide={() => setShowUploadLink(false)}
                                        title="Public Upload Link"
                                        getLink={() => createUploadLink({ clientId: client._id })}
                                    />
                                </>
                            ) : (
                                // Not onboarding:
                                <div className="info-section">
                                    <p className="info-field">
                                        <strong>Client Type:</strong>
                                        <span className="info-value">{camelCaseToText(client.clientType) || '-'}</span>
                                    </p>

                                    <p className="info-field">
                                        <strong>Date Of Birth:</strong>
                                        <span className="info-value">
                                            {client?.dateOfBirth ? (
                                                (() => {
                                                    const ageResult = calculateAge(client.dateOfBirth);
                                                    return (
                                                        <>
                                                            {formatDate(client?.dateOfBirth, true, false)} (age {ageResult.age})
                                                            {ageResult.isBirthday && (
                                                                <BirthdayIndicator daysUntilBirthday={ageResult.daysUntilBirthday!} />
                                                            )}
                                                        </>
                                                    );
                                                })()
                                            ) : null}
                                        </span>
                                    </p>

                                    <p className="info-field">
                                        <strong>Gender:</strong>
                                        <span className="info-value">{client?.gender}</span>
                                    </p>

                                    <p className="info-field">
                                        <strong>Marital Status:</strong>
                                        <span className="info-value">{client?.maritalStatus}</span>
                                    </p>

                                    <p className="info-field">
                                        <strong>Address:</strong>
                                        <span className="info-value">
                                            {client.address?.city ? (
                                                <>
                                                    {client.address.street} {client.address.houseNumber}, {client.address.city}
                                                </>
                                            ) : null}
                                        </span>
                                    </p>

                                    <p className="info-field">
                                        <strong>Employment Status:</strong>
                                        <span className="info-value">{camelCaseToText(client?.employmentStatus)}</span>
                                    </p>
                                </div>
                            )}
                        </div>
                    ) : null}

                    {
                        /* add employer modal */
                        showAddEmployer && (
                            <AddEmployer
                                show={showAddEmployer}
                                onHide={() => setShowAddEmployer(false)}
                                onAddEmployer={onExistingEmployerSelected}
                                newEmployerName={newEmployerName}
                            />
                        )
                    }
                </div>
            </Card.Body>
        </Card>
    );
};

export default ProfileBox;
